<template>
    <vx-table-row>
        <vx-table-row-title>
            <div class="flex items-center gap-3">
                <vx-badge color="slate" size="xs" data-test="migration-id">
                    {{ migration.id }}
                </vx-badge>
            </div>
        </vx-table-row-title>

        <vx-table-row-item data-test="team">
            <template v-slot:label> Team: </template>
            <div
                class="text-sm font-bold line-clamp-2"
                :title="`${migration.target_team_id} - ${migration.target_team_name}`"
            >
                <vx-badge color="slate" size="xs">{{ migration.target_team_id }}</vx-badge>
                {{ migration.target_team_name }}
            </div>
        </vx-table-row-item>

        <vx-table-row-item data-test="status">
            <template v-slot:label> Status: </template>
            <span>{{ getMigrationStatusLabel(migration.status) }}</span>
        </vx-table-row-item>

        <vx-table-row-item data-test="created-at">
            <template v-slot:label> Created: </template>

            <vx-popover hover :arrow="true" placement="top" :key="createdAtKey">
                {{ fromNow(migration.created_at) }}
                <template v-slot:content>
                    {{ shortDateTime(migration.created_at) }}
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-item data-test="updated-at">
            <template v-slot:label> Updated: </template>

            <vx-popover hover :arrow="true" placement="top" :key="updatedAtKey">
                {{ fromNow(migration.updated_at) }}
                <template v-slot:content>
                    {{ shortDateTime(migration.updated_at) }}
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-dropdown v-if="showActions" placement="right">
                <vx-button data-test="dropdown" size="sm" type="button" color="muted" outline class="w-9">
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item
                        v-if="migration.status === 'draft'"
                        @click="launchModal = true"
                        data-test="launch-action"
                    >
                        Launch
                        <font-awesome-icon widthAuto :icon="faRocket"></font-awesome-icon>
                    </vx-dropdown-item>

                    <vx-dropdown-item
                        v-if="migration.status === 'draft'"
                        @click="deleteModal = true"
                        data-test="delete-action"
                    >
                        Delete
                        <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                    </vx-dropdown-item>

                    <vx-dropdown-item
                        v-if="migration.status === 'in_progress'"
                        @click="cancelModal = true"
                        data-test="cancel-action"
                    >
                        Cancel
                        <font-awesome-icon widthAuto :icon="faBan"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>

            <vx-button :href="`/kiosk/migrations/${migration.id}`" size="sm" data-test="view" outline color="muted">
                View <font-awesome-icon widthAuto :icon="faEye" />
            </vx-button>
        </vx-table-row-buttons>

        <team-migration-launch-modal
            v-model:visible="launchModal"
            v-model:migration="migration"
        ></team-migration-launch-modal>
        <team-migration-cancel-modal
            v-model:visible="cancelModal"
            v-model:migration="migration"
        ></team-migration-cancel-modal>
        <team-migration-delete-modal
            v-model:visible="deleteModal"
            :migration="migration"
            @deleted="handleDeleted"
        ></team-migration-delete-modal>
    </vx-table-row>
</template>

<script setup>
import { faBan, faEllipsis, faEye, faRocket, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxPopover,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { computed, ref } from 'vue';
import { useInterval } from '@vueuse/core';
import { getMigrationStatusLabel } from '~/utils/migrationHelpers';
import { fromNow, shortDateTime } from '~/utils/date';
import TeamMigrationLaunchModal from '~/components/kiosk/kiosk-migrations/modals/TeamMigrationLaunchModal.vue';
import TeamMigrationCancelModal from '~/components/kiosk/kiosk-migrations/modals/TeamMigrationCancelModal.vue';
import TeamMigrationDeleteModal from '~/components/kiosk/kiosk-migrations/modals/TeamMigrationDeleteModal.vue';

const migration = defineModel({
    required: true,
    type: Object,
});

const emit = defineEmits(['deleted']);

const showActions = computed(() => ['draft', 'in_progress'].includes(migration.value.status));

const launchModal = ref(false);
const cancelModal = ref(false);
const deleteModal = ref(false);
const createdAtKey = useInterval(60000);
const updatedAtKey = useInterval(60000);

const handleDeleted = (migrationId) => {
    emit('deleted', migrationId);
};
</script>
